'use client';

import Accordion from '@enlighten/design-system/dist/Accordion';
import AccordionDetails from '@enlighten/design-system/dist/AccordionDetails';
import AccordionSummary from '@enlighten/design-system/dist/AccordionSummary';
import Grid from '@enlighten/design-system/dist/Grid2';
import type { Theme } from '@enlighten/design-system/dist/styles';
import Typography from '@enlighten/design-system/dist/Typography';
import useMediaQuery from '@enlighten/design-system/dist/useMediaQuery';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import ArrowRight from '@/assets/images/right.svg';
import { navigationList } from '@/constants/navigation';
import { fonts } from '@/styles/fonts';
import variables from '@/styles/variables.module.scss';

import SubMenuItem from './SubMenuItem';

export default function SiteMap() {
	const pathname = usePathname();
	const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

	return isMobile ? (
		<Grid width="100%" padding="25px 24px">
			{navigationList.map(({ title, linkProps, subMenuList }, i) => (
				<Accordion
					key={i}
					sx={{
						minHeight: 48,
						borderBottom: `1px solid ${variables.grey300}`,
						borderRadius: 0,
						boxShadow: 'none',
						'.MuiAccordionSummary-root': {
							height: 48,
							padding: '0 16px 0 0',
						},
						'&.Mui-expanded': {
							margin: 0,
							'.MuiAccordionSummary-root.Mui-expanded': {
								minHeight: 48,
								'*': {
									margin: 0,
								},
							},
						},
						'::before': {
							display: 'none',
						},
					}}
				>
					<AccordionSummary
						expandIcon={subMenuList.length > 0 ? <ArrowRight width={14} height={16} /> : undefined}
						sx={{
							a: {
								display: 'flex',
								alignItems: 'center',
								width: '100%',
								height: '100%',
							},
							svg: {
								transform: 'rotate(90deg)',
							},
							'.MuiAccordionSummary-content': {
								alignItems: 'center',
								height: '100%',
								margin: 0,
							},
						}}
					>
						{linkProps ? (
							<Link {...linkProps}>
								<Typography fontFamily={fonts.style.fontFamily} fontWeight={500} fontSize={16} data-testid={`title-${title}`}>
									{title}
								</Typography>
							</Link>
						) : (
							<Typography fontFamily={fonts.style.fontFamily} fontWeight={500} fontSize={16} data-testid={`title-${title}`}>
								{title}
							</Typography>
						)}
					</AccordionSummary>
					{subMenuList.length > 0 && (
						<AccordionDetails
							sx={{
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
								marginBottom: '9px',
								padding: 0,
								'> a': {
									display: 'flex',
									alignItems: 'center',
									width: '100%',
									height: 40,
									'&.active': {
										bgcolor: '#F4E6FF',
										'.MuiTypography-root': {
											color: 'primary.light',
										},
									},
									'&:hover .MuiTypography-root': {
										color: 'primary.main',
									},
									'.MuiTypography-root': {
										transition: 'color 0.3s ease',
									},
								},
							}}
						>
							{subMenuList.map(({ title, linkProps: { href, ...linkProps } }, j) => (
								<Link key={j} className={href === pathname ? 'active' : undefined} title={`sub-menu-${title}`} href={href} {...linkProps}>
									<Typography>{title}</Typography>
								</Link>
							))}
						</AccordionDetails>
					)}
				</Accordion>
			))}
		</Grid>
	) : (
		<Grid display="flex" maxWidth={1440} width="100%" padding="62px 24px 39px">
			{navigationList.map(({ title, linkProps, subMenuList }, i) => (
				<Grid key={i} maxWidth={174} width={subMenuList.length > 0 ? '100%' : 'fit-content'}>
					{linkProps ? (
						<Link {...linkProps}>
							<Typography fontWeight={700} fontSize={16} marginBottom="14px" data-testid={`title-${title}`}>
								{title}
							</Typography>
						</Link>
					) : (
						<Typography fontWeight={700} fontSize={16} marginBottom="14px" data-testid={`title-${title}`}>
							{title}
						</Typography>
					)}
					{subMenuList.length > 0 && (
						<Grid
							container
							direction="column"
							gap="12px"
							sx={{
								'a:hover': {
									textDecoration: 'underline',
								},
							}}
						>
							{subMenuList.map(({ title, linkProps }, j) => (
								<SubMenuItem key={j} title={title} linkProps={{ title: `sub-menu-${title}`, ...linkProps }} />
							))}
						</Grid>
					)}
				</Grid>
			))}
		</Grid>
	);
}
